<!-- Inline chat "status event" card (e.g. doctor joined, subscription started): a leading media badge beside a caption/title/subtitle text column, toned to signal the event kind. -->

<script lang="ts" setup>
import { defineProp } from '@nzyme/vue-utils/defineProp.js';
import type { VNodeChild } from 'vue';

// Types
type StatusCardTone = 'blue' | 'green';

// Props
const props = defineProps({
  caption: { type: String, required: true },
  title: { type: String, required: true },
  tone: defineProp<StatusCardTone>({ default: 'blue' }),
  /**
   * When true, render a shimmer skeleton in place of the title instead of
   * inferring "still loading" from an empty title. Drive it from an explicit
   * signal such as a data source's `loaded` flag.
   */
  loading: { type: Boolean, default: false },
});

const slots = defineSlots<{
  media: () => VNodeChild;
  subtitle: () => VNodeChild;
}>();
</script>

<template>
  <div :class="[css.card, css[`tone-${props.tone}`]]">
    <div :class="css.media">
      <slot name="media" />
    </div>

    <div :class="css.text">
      <span :class="css.caption">{{ caption }}</span>
      <span
        v-if="loading"
        :class="css.titleSkeleton"
      />
      <span
        v-else
        :class="css.title"
        >{{ title }}</span
      >
      <span
        v-if="!loading && slots.subtitle"
        :class="css.subtitle"
      >
        <slot name="subtitle" />
      </span>
    </div>
  </div>
</template>

<style lang="scss" module="css">
.card {
  display: flex;
  align-items: center;
  gap: $spacing-14;
  padding: $spacing-16;
  width: 100%;
  background: $color-white;
  border: 1px solid var(--status-card-border);
  border-radius: $border-radius-16;

  --status-card-border: #{$color-doctor-200};
  --status-card-caption: #{$color-doctor-600};
}

.tone-green {
  --status-card-border: #{$color-gray-100};
  --status-card-caption: #{$color-green-500};
}

.media {
  flex-shrink: 0;
}

.text {
  display: flex;
  flex-direction: column;
  gap: $spacing-02;
  flex: 1;
  min-width: 0;
}

.caption {
  font-size: $font-size-10;
  font-weight: $font-weight-700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--status-card-caption);
}

.title {
  font-size: $font-size-15;
  font-weight: $font-weight-600;
  color: $color-text;
}

.subtitle {
  font-size: $font-size-12;
  font-weight: $font-weight-400;
  color: $color-text-secondary;
}

.titleSkeleton {
  width: 60%;
  height: $font-size-15;
  margin: 2px 0;
  border-radius: $border-radius-04;
  background: linear-gradient(
    115deg,
    $color-gray-100 0%,
    $color-gray-100 25%,
    $color-gray-200 50%,
    $color-gray-100 75%,
    $color-gray-100 100%
  );
  background-size: 200% 200%;
  animation: status-card-shimmer 1.5s linear infinite;
}

@keyframes status-card-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}
</style>
